import './mediaAccess';
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import m from 'mithril';
import Button from 'flarum/common/components/Button';
import Avatar from 'flarum/common/components/Avatar';
import editUserIdentity from '../editUserIdentity';
const profiles = new Map();
function publicProfile(username) {
  const cached = profiles.get(username);
  if (cached && Date.now() - cached.time < 60000) return cached.promise;
  const promise = fetch(`https://id-api.hustab.com/user/info?username=${encodeURIComponent(username)}`, {
    credentials: 'omit',
    redirect: 'error',
    signal: AbortSignal.timeout(6000)
  }).then(async response => {
    if (!response.ok) throw new Error('Profile unavailable');
    const {
      data
    } = await response.json();
    if (data?.username !== username) throw new Error('Profile identity mismatch');
    return {
      name: typeof data.name === 'string' ? data.name.slice(0, 150) : username,
      avatarUrl: safeAvatar(data.avatarUrl),
      bio: typeof data.bio === 'string' ? data.bio.slice(0, 160) : '',
      badges: Array.isArray(data.badges) ? data.badges.filter(v => typeof v === 'string' && v.trim()).slice(0, 30) : []
    };
  }).catch(() => null);
  profiles.set(username, {
    promise,
    time: Date.now()
  });
  return promise;
}
function safeAvatar(url) {
  if (typeof url !== 'string') return '';
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !u.username && !u.password ? u.href : '';
  } catch {
    return '';
  }
}
function login() {
  location.assign(`${app.forum.attribute('baseUrl')}/auth/hustab?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
}
function tr(key) {
  return app.translator.trans(`hustab-space.forum.${key}`);
}
function loginBody() {
  return m('.Modal-body', [m(Button, {
    className: 'Button Button--primary Button--block',
    onclick: login
  }, tr('login')), m('p.HustabLogin-note', tr('login_note'))]);
}
function refreshUser(user) {
  const username = user?.attribute('hustabUsername');
  if (!username) return;
  publicProfile(username).then(profile => {
    if (!profile) return;
    user.pushAttributes({
      displayName: profile.name,
      avatarUrl: profile.avatarUrl || null,
      avatarSrcset: null,
      hustabBio: profile.bio,
      hustabBadges: profile.badges
    });
    m.redraw();
  });
}
app.initializers.add('hustab-space', () => {
  editUserIdentity();
  // Initializers run before forum and session hydration. Read them only once
  // Flarum is ready to mount, and give its home-link handler to the Space link.
  app.beforeMount(() => {
    const brandLink = document.querySelector('.Header-title #home-link');
    if (brandLink && !document.querySelector('.SpaceBrandLink')) {
      brandLink.id = 'hustab-home-link';
      brandLink.href = 'https://hustab.com/';
      brandLink.setAttribute('aria-label', 'HUST AI Builders 官网');
      const spaceLink = document.createElement('a');
      spaceLink.id = 'home-link';
      spaceLink.className = 'SpaceBrandLink';
      spaceLink.href = app.forum.attribute('baseUrl') + '/';
      spaceLink.setAttribute('aria-label', 'SPACE 共创空间');
      spaceLink.setAttribute('data-space-version', 'native-20261024');
      spaceLink.innerHTML = '<span class="SpaceBrandLink-title">SPACE</span><span class="SpaceBrandLink-caption">共创空间</span>';
      brandLink.parentElement?.append(spaceLink);
    }
    refreshUser(app.session.user);
    window.addEventListener('focus', () => {
      profiles.clear();
      refreshUser(app.session.user);
    });
  });
  extend('flarum/forum/components/HeaderSecondary', 'items', function (items) {
    if (app.session.user) return;
    items.remove('signUp');
    items.setContent('logIn', m(Button, {
      className: 'Button Button--primary',
      onclick: login
    }, tr('login')));
  });
  // Modal and settings modules are lazy-loaded in Flarum 2. Register hooks by
  // module path so they are applied when those components actually load.
  for (const modal of ['LogInModal', 'SignUpModal']) {
    const path = `flarum/forum/components/${modal}`;
    override(path, 'content', loginBody);
    override(path, 'onsubmit', function (_original, event) {
      event.preventDefault();
      login();
    });
  }
  // Keep Flarum's own card structure, hover detection, placement, and fade animation.
  // Only replace the identity details inside the native popover.
  override('flarum/forum/components/AvatarEditor', 'view', function () {
    return m('div', {
      className: this.attrs.className
    }, m(Avatar, {
      user: this.attrs.user,
      loading: 'eager'
    }));
  });
  extend('flarum/forum/components/UserCard', 'oninit', function () {
    refreshUser(this.attrs.user);
  });
  extend('flarum/forum/components/UserCard', 'contentItems', function (items) {
    const user = this.attrs.user;
    const groupBadges = items.has('badges') ? items.get('badges') : null;
    if (!String(this.attrs.className || '').includes('UserCard--popover')) {
      items.add('hustabHandle', m('span.HustabMemberCard-handle', '@' + (user.attribute('hustabUsername') || user.username())), 95);
      const badges = user.attribute('hustabBadges') || [];
      if (badges.length || groupBadges) items.setContent('identity', m('.HustabMemberCard-nameRow.HustabMemberCard-nameRow--profile', [items.get('identity'), ...badges.map(badge => m('span.HustabMemberCard-badge', badge)), groupBadges]));
      items.remove('badges');
      const bio = user.attribute('hustabBio');
      if (bio) items.add('hustabBio', m('p.HustabMemberCard-bio', bio), 85);
      return;
    }
    if (!user.attribute('hustabUsername')) return;
    items.remove('identity');
    items.remove('badges');
    items.remove('info');
    const username = user.attribute('hustabUsername');
    const name = user.displayName() || username;
    const badges = user.attribute('hustabBadges') || [];
    const bio = user.attribute('hustabBio');
    items.add('hustabIdentity', m('.HustabMemberCard-nameRow', [m('a.HustabMemberCard-name', {
      href: app.route.user(user)
    }, name), ...badges.map(badge => m('span.HustabMemberCard-badge', badge)), groupBadges]), 100);
    items.add('hustabHandle', m('span.HustabMemberCard-handle', '@' + username), 90);
    if (bio) items.add('hustabBio', m('p.HustabMemberCard-bio', bio), 80);
  });
  extend('flarum/forum/components/SettingsPage', 'accountItems', function (items) {
    if (!app.session.user?.attribute('hustabUsername')) return;
    for (const key of ['changeEmail', 'changePassword']) items.remove(key);
    items.add('hustabAccount', m('a.Button', {
      href: 'https://id.hustab.com/account',
      target: '_blank',
      rel: 'noopener noreferrer'
    }, tr('manage_profile')), 100);
  });
  extend('flarum/forum/components/SettingsPage', 'dataItems', function (items) {
    items?.remove('gdprErasure');
  });
}, -100);